import { getAuth } from 'firebase/auth';
import { userService } from '../firebase/user.service';

// Types d'abonnements disponibles
export type SubscriptionType = 'free' | 'essential' | 'pro';

// Interface pour la configuration des limites et fonctionnalités par type d'abonnement
export interface SubscriptionLimits {
  type: SubscriptionType;
  maxGuests: number;
  maxTasks: number;
  maxVendors: number;
  maxCollaborators: number;
  maxBudgetItems: number;
  maxTimelineEvents: number;
  maxEvents: number;
  features: {
    exportPDF: boolean;
    exportExcel: boolean;
    prioritySupport: boolean;
    guestGroups: boolean;
    seatingPlanner: boolean;
    budgetAnalytics: boolean;
    advancedAnalytics: boolean;
    mobileApp: boolean;
    multiLanguage: boolean;
    multipleEvents: boolean;
    customProfilePhoto: boolean;
  };
}

// Configuration des limites pour chaque type d'abonnement
export const SUBSCRIPTION_LIMITS: Record<SubscriptionType, SubscriptionLimits> = {
  free: {
    type: 'free',
    maxGuests: 50,
    maxTasks: 20,
    maxVendors: 10,
    maxCollaborators: 2,
    maxBudgetItems: 15,
    maxTimelineEvents: 10,
    maxEvents: 1,
    features: {
      exportPDF: false,
      exportExcel: false,
      prioritySupport: false,
      guestGroups: false,
      seatingPlanner: false,
      budgetAnalytics: false,
      advancedAnalytics: false,
      mobileApp: true,
      multiLanguage: true,
      multipleEvents: false,
      customProfilePhoto: false
    }
  },
  essential: {
    type: 'essential',
    maxGuests: 200,
    maxTasks: 100,
    maxVendors: 30,
    maxCollaborators: 5,
    maxBudgetItems: 50,
    maxTimelineEvents: 30,
    maxEvents: 3,
    features: {
      exportPDF: true,
      exportExcel: true,
      prioritySupport: false,
      guestGroups: true,
      seatingPlanner: true,
      budgetAnalytics: false,
      advancedAnalytics: false,
      mobileApp: true,
      multiLanguage: true,
      multipleEvents: false,
      customProfilePhoto: true
    }
  },
  pro: {
    type: 'pro',
    maxGuests: Infinity,
    maxTasks: 500,
    maxVendors: 100,
    maxCollaborators: 10,
    maxBudgetItems: 200,
    maxTimelineEvents: 100,
    maxEvents: Infinity,
    features: {
      exportPDF: true,
      exportExcel: true,
      prioritySupport: true,
      guestGroups: true,
      seatingPlanner: true,
      budgetAnalytics: true,
      advancedAnalytics: true,
      mobileApp: true,
      multiLanguage: true,
      multipleEvents: true,
      customProfilePhoto: true
    }
  }
};

// Service de gestion des abonnements
class SubscriptionService {
  // Récupérer l'abonnement actuel de l'utilisateur
  async getCurrentSubscription(userId?: string): Promise<SubscriptionLimits> {
    try {
      // Si aucun userId n'est fourni, essayer de récupérer l'utilisateur connecté
      if (!userId) {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
          // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log('Aucun utilisateur connecté, utilisation de l\'abonnement gratuit par défaut');
          return SUBSCRIPTION_LIMITS.free;
        }
        
        userId = currentUser.uid;
      }
      
      // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log(`Recherche du profil pour l'utilisateur: ${userId}`);
      
      try {
        // D'abord, essayons de créer un profil utilisateur minimal
        // pour éviter tout problème si getUserProfile lève une exception
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (currentUser && currentUser.uid === userId) {
          try {
            // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log(`Initialisation automatique du profil utilisateur pour ${userId}`);
            
            // Créer un profil minimal
            const basicProfile = {
              id: currentUser.uid,
              email: currentUser.email || '',
              displayName: currentUser.displayName || '',
              partner1: {
                firstName: '',
                lastName: ''
              },
              partner2: {
                firstName: '',
                lastName: ''
              },
              weddingDetails: {
                date: new Date(),
                location: { city: '' },
                budget: 0,
                guestCount: 0
              },
              createdAt: new Date(),
              updatedAt: new Date(),
              onboardingCompleted: false,
              subscription: 'free'
            };
            
            // Utiliser createUserProfile qui vérifie si le profil existe déjà
            // et ne fera rien s'il existe déjà
            await userService.createUserProfile(currentUser.uid, basicProfile);
          } catch (initError) {
            console.warn(`Erreur lors de l'initialisation préventive du profil: ${initError}`);
            // On continue quand même pour tenter de récupérer le profil
          }
        }
        
        // Ensuite, essayons de récupérer le profil
        let userProfile = null;
        try {
          userProfile = await userService.getUserProfile(userId);
        } catch (profileError) {
          console.error(`Erreur lors de la récupération du profil: ${profileError}`);
          userProfile = null;
        }
        
        if (!userProfile) {
          // Si on n'a pas de profil, retourner l'abonnement gratuit
          return SUBSCRIPTION_LIMITS.free;
        }

        // NOUS VÉRIFIONS L'ABONNEMENT REEL VIA L'EXTENSION STRIPE
        try {
          const { firebaseStripeService, STRIPE_PRODUCTS } = await import('../firebase-stripe.service');
          const activeStripeSub = await firebaseStripeService.getActiveSubscription(userId);
          
          if (activeStripeSub) {
            // Déterminer si c'est pro ou essential basé sur le priceId ou le rôle
            let activeType: SubscriptionType = 'free';
            
            if (activeStripeSub.priceId === STRIPE_PRODUCTS.pro.priceId || activeStripeSub.metadata?.['subscriptionType'] === 'pro') {
              activeType = 'pro';
            } else if (activeStripeSub.priceId === STRIPE_PRODUCTS.essential.priceId || activeStripeSub.metadata?.['subscriptionType'] === 'essential') {
              activeType = 'essential';
            } else if (activeStripeSub.metadata?.['subscriptionType']) {
              activeType = activeStripeSub.metadata['subscriptionType'] as SubscriptionType;
            }
            
            if (activeType !== 'free') {
              // Si le profil utilisateur n'est pas synchronisé, on le met à jour silencieusement
              if (userProfile['subscription'] !== activeType) {
                userService.updateUserProfile(userId, { subscription: activeType }).catch(e => console.error(e));
              }
              return SUBSCRIPTION_LIMITS[activeType];
            }
          } else {
            // Si pas d'abonnement actif Stripe mais le profil dit autre chose, on downgrade
            if (userProfile['subscription'] !== 'free') {
               userService.updateUserProfile(userId, { subscription: 'free' }).catch(e => console.error(e));
            }
            return SUBSCRIPTION_LIMITS.free;
          }
        } catch (stripeError) {
          console.error('Erreur lors de la vérification de l\'abonnement Stripe réel:', stripeError);
        }
        
        // // // // // // // // // // // // // // // // // // // // // // // // // // // // console.log(`Profil trouvé pour l'utilisateur ${userId}, type d'abonnement:`, userProfile.subscription || 'free');
        const subscriptionType = userProfile['subscription'] || 'free';
        return SUBSCRIPTION_LIMITS[subscriptionType as SubscriptionType];
      } catch (error) {
        console.error('Erreur lors de la récupération du profil utilisateur:', error);
        // Ne pas lever d'exception, retourner simplement l'abonnement gratuit
        return SUBSCRIPTION_LIMITS.free;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'abonnement:', error);
      // Par défaut, retourner l'abonnement gratuit en cas d'erreur
      return SUBSCRIPTION_LIMITS.free;
    }
  }
  
  // Vérifier si l'utilisateur a accès à une fonctionnalité spécifique
  async hasFeatureAccess(userId: string, feature: keyof SubscriptionLimits['features']): Promise<boolean> {
    try {
      const subscription = await this.getCurrentSubscription(userId);
      
      return subscription.features[feature];
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'accès à la fonctionnalité:', error);
      return false;
    }
  }
  
  // Vérifier si l'utilisateur a atteint une limite de ressource
  async checkLimit(userId: string, resourceType: 'maxGuests' | 'maxTasks' | 'maxVendors' | 'maxCollaborators' | 'maxBudgetItems' | 'maxTimelineEvents' | 'maxEvents', currentCount: number): Promise<{ hasReachedLimit: boolean; max: number }> {
    try {
      const subscription = await this.getCurrentSubscription(userId);
      
      const maxAllowed = subscription[resourceType];
      const hasReachedLimit = currentCount >= maxAllowed;
      
      return { hasReachedLimit, max: maxAllowed };
    } catch (error) {
      console.error('Erreur lors de la vérification de la limite:', error);
      // En cas d'erreur, utiliser les limites de l'abonnement gratuit
      const maxAllowed = SUBSCRIPTION_LIMITS.free[resourceType];
      const hasReachedLimit = currentCount >= maxAllowed;
      
      return { hasReachedLimit, max: maxAllowed };
    }
  }
  
  // Mettre à jour l'abonnement de l'utilisateur
  async updateSubscription(userId: string, subscriptionType: SubscriptionType): Promise<void> {
    try {
      // Vérifier d'abord si le profil utilisateur existe
      const currentProfile = await userService.getUserProfile(userId);

      if (!currentProfile) {
        console.error(`❌ [SubscriptionService] Profil utilisateur non trouvé pour ${userId}`);
        throw new Error(`Profil utilisateur non trouvé pour ${userId}`);
      }

      await userService.updateUserProfile(userId, {
        subscription: subscriptionType,
        updatedAt: new Date()
      });

      // Vérifier que la mise à jour a bien eu lieu
      const updatedProfile = await userService.getUserProfile(userId);

      if (updatedProfile?.['subscription'] !== subscriptionType) {
        console.error(`❌ [SubscriptionService] La mise à jour a échoué. Attendu: ${subscriptionType}, Obtenu: ${updatedProfile?.['subscription']}`);
        throw new Error(`La mise à jour de l'abonnement a échoué`);
      }
    } catch (error) {
      console.error('❌ [SubscriptionService] Erreur lors de la mise à jour de l\'abonnement:', error);
      throw error;
    }
  }
  
  // Vérifier si un utilisateur doit passer à un abonnement supérieur pour une fonctionnalité
  async shouldUpgradeForFeature(userId: string, feature: keyof SubscriptionLimits['features']): Promise<{ shouldUpgrade: boolean; suggestedPlan: SubscriptionType | null }> {
    try {
      const currentSubscription = await this.getCurrentSubscription(userId);
      
      if (!currentSubscription) {
        // Si pas d'abonnement, suggérer Essential si la fonctionnalité y est disponible
        if (SUBSCRIPTION_LIMITS.essential.features[feature]) {
          return { shouldUpgrade: true, suggestedPlan: 'essential' };
        }
        // Sinon suggérer Pro
        return { shouldUpgrade: true, suggestedPlan: 'pro' };
      }
      
      // Si la fonctionnalité est déjà disponible, pas besoin de mise à niveau
      if (currentSubscription.features[feature]) {
        return { shouldUpgrade: false, suggestedPlan: null };
      }
      
      // Déterminer le plan minimum requis pour cette fonctionnalité
      if (SUBSCRIPTION_LIMITS.essential.features[feature] && currentSubscription.type !== 'essential' && currentSubscription.type !== 'pro') {
        return { shouldUpgrade: true, suggestedPlan: 'essential' };
      }
      
      if (SUBSCRIPTION_LIMITS.pro.features[feature]) {
        return { shouldUpgrade: true, suggestedPlan: 'pro' };
      }
      
      // Si aucun plan n'offre la fonctionnalité (cas rare)
      return { shouldUpgrade: false, suggestedPlan: null };
    } catch (error) {
      console.error('Erreur lors de la vérification de la nécessité de mise à niveau:', error);
      return { shouldUpgrade: false, suggestedPlan: null };
    }
  }
  
  // Vérifier le niveau minimum d'abonnement requis pour une fonctionnalité
  getMinimumPlanForFeature(feature: keyof SubscriptionLimits['features']): SubscriptionType | null {
    if (SUBSCRIPTION_LIMITS.free.features[feature]) {
      return 'free';
    }
    
    if (SUBSCRIPTION_LIMITS.essential.features[feature]) {
      return 'essential';
    }
    
    if (SUBSCRIPTION_LIMITS.pro.features[feature]) {
      return 'pro';
    }
    
    return null;
  }
}

export const subscriptionService = new SubscriptionService();

// Exportation
export default subscriptionService; 